import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import * as React from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { permissionDisplayName } from '../../../../../permissions';
import RolesAPI, { Role } from '../../api/roles';
import {
	DELETE_CONFIRM_MESSAGE,
	ROLES_ACTIONS_DELETE_TOOLTIP_TEXT,
	ROLES_ACTIONS_EDIT_TOOLTIP_TEXT,
	ROLES_ADD_ROLE_HEADER,
	ROLES_CONFIRM_DELETE_MODAL_CANCEL_BUTTON,
	ROLES_CONFIRM_DELETE_MODAL_DELETE_BUTTON,
	ROLES_CONFIRM_DELETE_MODAL_MESSAGE,
	ROLES_HEADER,
} from '../../data/constants';
import { handleModalClose } from '../../utils/handleModalClose';
import TableHeader, { HeadCell, Order } from './components/TableHeader';

const headCells: HeadCell[] = [
	{
		id: 'displayName',
		label: 'Display Name',
	},
	{
		id: 'name',
		label: 'Name',
	},
	{
		id: 'permissions',
		label: 'Permissions',
	},
	{
		id: 'actions',
		label: 'Actions',
	},
];

const Roles = () => {
	const navigate = useNavigate();
	const { tenantHostname } = useParams();
	const authData = useAuthContext();
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<keyof Role>('name');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);
	const [tablePendingDelete, setTablePendingDelete] = React.useState<Role>();
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		React.useState(false);
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { data, isLoading } = RolesAPI.useRoles(
		[RolesAPI.ROLES_KEY, tenantId || tenantHostname],
		tenantHostname,
		authToken,
	);

	const { mutate: deleteRole } = RolesAPI.useDeleteRole(
		[RolesAPI.ROLES_KEY, tenantId || tenantHostname],
		tenantHostname,
		authToken,
	);

	const handleRoleDelete = (roleName: string) => {
		deleteRole(roleName);
		handleConfirmDeleteModalClose();
	};

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof Role,
	) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleConfirmDeleteModalOpen = (selectedRole: Role) => {
		setIsConfirmDeleteModalOpen(true);
		setTablePendingDelete(selectedRole);
	};

	const handleConfirmDeleteModalClose = () => {
		setIsConfirmDeleteModalOpen(false);
		setTablePendingDelete(null);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<>
			<Stack direction='column' height='100%' spacing={2}>
				<Stack direction='row' spacing={2}>
					<Typography variant='h3'>{ROLES_HEADER()}</Typography>
					<Button
						component={NavLink}
						variant='contained'
						color='secondary'
						startIcon={<AddIcon />}
						to='new'
					>
						{ROLES_ADD_ROLE_HEADER()}
					</Button>
				</Stack>
				<Paper
					sx={{
						flex: 1,
						overflowY: 'auto',
					}}
				>
					<TableContainer sx={{ maxHeight: '100%' }}>
						<Table size='small' stickyHeader>
							<TableHeader
								headCells={headCells}
								order={order}
								orderBy={orderBy}
								onRequestSort={handleRequestSort}
							/>
							<TableBody>
								{data?.roles
									?.sort((a, b) => {
										const direction = order === 'asc' ? 1 : -1;
										if (a[orderBy] < b[orderBy]) {
											return -1 * direction;
										}
										if (a[orderBy] > b[orderBy]) {
											return 1 * direction;
										}
										return 0;
									})
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((role, index) => (
										<TableRow
											key={role.name}
											hover
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell sx={{ verticalAlign: 'baseline' }}>
												{role.displayName}
											</TableCell>
											<TableCell sx={{ verticalAlign: 'baseline' }}>
												{role.name}
											</TableCell>
											<TableCell sx={{ maxWidth: 8 * 40 }}>
												{role.permissions.sort().map((permission, index) => (
													<Chip
														size='small'
														color='secondary'
														variant='filled'
														key={permission + '.' + index}
														label={permissionDisplayName(permission)}
														sx={{ m: 0.5 }}
													/>
												))}
											</TableCell>
											<TableCell sx={{ verticalAlign: 'baseline' }}>
												<Stack
													direction='row'
													spacing={1}
													justifyContent='flex-end'
												>
													<Tooltip title={ROLES_ACTIONS_EDIT_TOOLTIP_TEXT()}>
														<IconButton
															aria-label='edit'
															onClick={() => navigate(`edit/${role.name}`)}
														>
															<EditIcon />
														</IconButton>
													</Tooltip>
													<Tooltip title={ROLES_ACTIONS_DELETE_TOOLTIP_TEXT()}>
														<IconButton
															aria-label='delete'
															onClick={() => handleConfirmDeleteModalOpen(role)}
														>
															<DeleteIcon />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
							{!data && isLoading && (
								<>
									<TableRow>
										<TableCell colSpan={4}>
											<Skeleton width='100%' height={52} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={4}>
											<Skeleton width='100%' height={52} />
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={4}>
											<Skeleton width='100%' height={52} />
										</TableCell>
									</TableRow>
								</>
							)}
							{data && !isLoading && data?.roles?.length > 0 && (
								<TableFooter>
									<TableRow>
										<TableCell
											colSpan={4}
											sx={{
												backgroundColor: 'white.lightest',
												position: 'sticky',
												bottom: 0,
												borderTop: '1px solid',
												borderColor: 'white.dark',
											}}
										>
											<TablePagination
												rowsPerPageOptions={[10, 25, 50]}
												component='div'
												count={data?.roles?.length}
												rowsPerPage={rowsPerPage}
												page={page}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
											/>
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</TableContainer>
				</Paper>
			</Stack>
			<Dialog
				open={isConfirmDeleteModalOpen}
				onClose={(e, r) => handleModalClose(r, handleConfirmDeleteModalClose)}
			>
				<DialogTitle>{ROLES_ACTIONS_DELETE_TOOLTIP_TEXT()}</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					{`${ROLES_CONFIRM_DELETE_MODAL_MESSAGE(
						tablePendingDelete?.name,
					)} ${DELETE_CONFIRM_MESSAGE()}`}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmDeleteModalClose}>
						{ROLES_CONFIRM_DELETE_MODAL_CANCEL_BUTTON()}
					</Button>
					<Button
						variant='contained'
						color='error'
						onClick={() => handleRoleDelete(tablePendingDelete.name)}
					>
						{ROLES_CONFIRM_DELETE_MODAL_DELETE_BUTTON()}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default Roles;
